import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  useTheme,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

const includedFeatures = [
  "Full POS System with Advanced Features",
  "Unlimited Staff Accounts & Role Management",
  "Real-time Sales Analytics & Dashboards",
  "Comprehensive Inventory Management",
  "Multi-location Support & Branch Management",
  "Advanced Financial Reporting & Exports",
  "Customer Relationship Management (CRM)",
  "Loyalty Program & Points System",
  "Automated Stock Alerts & Reordering",
  "Secure Payment Processing Integration",
  "Custom Branding & Receipt Customization",
  "Mobile App Access for Management",
  "Priority 24/7 Technical Support",
  "Regular Feature Updates & Enhancements",
  "Data Backup & Security Measures",
  "API Access for Custom Integrations",
];

const SubscriptionPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubscribe = () => {
    navigate("/subscription/checkout", {
      state: {
        planDetails: {
          title: "Enterprise",
          price: "BHD 25",
          period: "month",
        },
      },
    });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 8, sm: 12 },
        px: { xs: 2, sm: 3, lg: 4 },
      }}
    >
      {/* Header Section */}
      <Box sx={{ maxWidth: "4xl", mx: "auto", textAlign: { sm: "center" } }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "2.5rem", sm: "3.75rem" },
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "text.primary",
            mb: 3,
            lineHeight: 1.2,
          }}
        >
          Unlock Full Access to VenFlows
        </Typography>
        <Typography
          sx={{
            maxWidth: "2xl",
            mx: "auto",
            mt: 3,
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            lineHeight: 1.7,
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          Transform your business with our comprehensive POS solution. Get
          immediate access to all premium features and take control of your
          operations with our enterprise-grade tools.
        </Typography>
      </Box>

      {/* Pricing Card */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: "2xl",
          mx: "auto",
          mt: { xs: 6, sm: 8 },
          borderRadius: "24px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: { lg: "flex" },
        }}
      >
        {/* Features Section */}
        <Box sx={{ p: { xs: 4, sm: 5 }, flexGrow: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: "2rem",
              fontWeight: 600,
              color: "text.primary",
              letterSpacing: "-0.01em",
            }}
          >
            Enterprise Suite
          </Typography>
          <Typography
            sx={{
              mt: 3,
              fontSize: "1rem",
              lineHeight: 1.75,
              color: "text.secondary",
            }}
          >
            Experience the full power of VenFlows with our comprehensive
            enterprise solution. Designed for businesses that demand excellence,
            scalability, and premium support.
          </Typography>

          {/* Features List */}
          <Box sx={{ mt: 5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                Premium Features Included
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Stack>

            <Box
              sx={{
                mt: 4,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: { xs: 2, sm: 3 },
              }}
            >
              {includedFeatures.map((feature) => (
                <Stack
                  key={feature}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  }}
                >
                  <CheckIcon
                    sx={{
                      width: 20,
                      height: 20,
                      color: "primary.main",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2">{feature}</Typography>
                </Stack>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Pricing Section */}
        <Box
          sx={{
            width: { lg: "33.333333%" },
            p: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: { xs: 4, sm: 5 },
              bgcolor: "grey.50",
              borderRadius: "16px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ px: 2, textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                Professional Monthly Plan
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                justifyContent="center"
                sx={{ mt: 3 }}
              >
                <Typography
                  sx={{
                    fontSize: "3.75rem",
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1,
                  }}
                >
                  25
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  BHD/mo
                </Typography>
              </Stack>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubscribe}
                sx={{
                  mt: 5,
                  mb: 3,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Activate Subscription
              </Button>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  lineHeight: 1.5,
                }}
              >
                Instant access upon payment confirmation
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubscriptionPage;
